"use client";

import { useId, useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

interface NavSvgIconProps {
  size?: number;
  className?: string;
  play?: boolean;
}

// Closed parallelograms (top, left, bottom, right corners). Side and bottom corners
// match the original SVG; top corners derived from parallelogram symmetry around the
// side y-level (each layer has half-height 6.53).
const TOP_CORNERS = [
  { x: 13.93, y: 0.15 },
  { x: 0.15, y: 6.68 },
  { x: 13.93, y: 13.21 },
  { x: 27.72, y: 6.68 },
];
const MIDDLE_CORNERS = [
  { x: 13.93, y: 5.54 },
  { x: 0.15, y: 12.07 },
  { x: 13.93, y: 18.6 },
  { x: 27.72, y: 12.07 },
];
const BOTTOM_CORNERS = [
  { x: 13.93, y: 10.94 },
  { x: 0.15, y: 17.47 },
  { x: 13.93, y: 24 },
  { x: 27.72, y: 17.47 },
];

const pointsString = (corners: { x: number; y: number }[], dy = 0) =>
  corners.map(({ x, y }) => `${x},${(y + dy).toFixed(3)}`).join(" ");

const INITIAL_TOP = pointsString(TOP_CORNERS);
const INITIAL_MIDDLE = pointsString(MIDDLE_CORNERS);
const INITIAL_BOTTOM = pointsString(BOTTOM_CORNERS);

export function PlatformsIcon({ size = 66, className, play = true }: NavSvgIconProps) {
  const reactId = useId();
  const middleMaskId = `platforms-middle-mask-${reactId.replace(/:/g, "")}`;
  const bottomMaskId = `platforms-bottom-mask-${reactId.replace(/:/g, "")}`;

  const containerRef = useRef<SVGSVGElement | null>(null);
  const topRef = useRef<SVGPolygonElement | null>(null);
  const topMaskARef = useRef<SVGPolygonElement | null>(null);
  const topMaskBRef = useRef<SVGPolygonElement | null>(null);
  const bottomRef = useRef<SVGPolygonElement | null>(null);

  useGSAP(
    () => {
      if (!play) return;
      const top = topRef.current;
      const topMaskA = topMaskARef.current;
      const topMaskB = topMaskBRef.current;
      const bottom = bottomRef.current;
      if (!top || !topMaskA || !topMaskB || !bottom) return;

      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const state = { topDy: 0, bottomDy: 0 };

        const apply = () => {
          const topPts = pointsString(TOP_CORNERS, state.topDy);
          const bottomPts = pointsString(BOTTOM_CORNERS, state.bottomDy);
          top.setAttribute("points", topPts);
          topMaskA.setAttribute("points", topPts);
          topMaskB.setAttribute("points", topPts);
          bottom.setAttribute("points", bottomPts);
        };

        gsap.to(state, {
          topDy: -1.5,
          bottomDy: 1.5,
          duration: 1.75,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
          onUpdate: apply,
        });
      });

      return () => mm.revert();
    },
    { scope: containerRef, dependencies: [play] }
  );

  return (
    <svg
      ref={containerRef}
      viewBox="0 0 27.87 24.15"
      width={size}
      height={size}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      overflow="visible"
      aria-hidden="true"
    >
      <defs>
        <mask id={middleMaskId}>
          <rect x="-10" y="-10" width="47.87" height="44.15" fill="white" />
          <polygon ref={topMaskARef} points={INITIAL_TOP} fill="black" />
        </mask>
        <mask id={bottomMaskId}>
          <rect x="-10" y="-10" width="47.87" height="44.15" fill="white" />
          <polygon ref={topMaskBRef} points={INITIAL_TOP} fill="black" />
          <polygon points={INITIAL_MIDDLE} fill="black" />
        </mask>
      </defs>

      <polygon ref={bottomRef} points={INITIAL_BOTTOM} mask={`url(#${bottomMaskId})`} />
      <polygon points={INITIAL_MIDDLE} mask={`url(#${middleMaskId})`} />
      <polygon ref={topRef} points={INITIAL_TOP} />
    </svg>
  );
}
